import React, { FC, ReactElement, useEffect, useState } from 'react';

import { Router, Route, Switch } from 'wouter';
import { SWRConfig } from 'swr';

import 'mintel-react-ui/dist/package/style.css';
import 'mintel-tokens/dist/assets/fonts.css';

import CwaArticleGetter from '/components/CwaArticleGetter';
import Navigation from '/components/Navigation';
import { AppShellContext } from '/utils/contexts';
import { Error404 } from '/utils/HttpError';
import { i18n } from '/utils/translations';
import UserAccessRestrictionProvider from '/utils/UserAccessRestrictionProvider';

import { AppShellProps } from '../types';
import AuthWrapper from './AuthWrapper';
import TrackingContext from '../components/trackingContext';

const enableMocking = async (): Promise<
  ServiceWorkerRegistration | undefined
> => {
  // Enable mocking using Mock Service Worker (MSW).
  // https://mswjs.io/
  if (BUILD_TARGET !== 'localdev-mock') {
    return;
  }
  const { worker } = await import('../mocks/browser');
  return worker.start();
};

const App: FC<AppShellProps> = ({ startTracking, ...props }) => {
  const lang = props.accessDetails?.mntl?.lang || 'en';
  const [waitForMSW, setWaitForMSW] = useState(
    BUILD_TARGET === 'localdev-mock',
  );

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang]);

  useEffect(() => {
    if (waitForMSW) {
      enableMocking().then(() => setWaitForMSW(false));
    }
  }, [waitForMSW]);

  if (waitForMSW) return <></>;

  return (
    <SWRConfig
      value={{
        provider: () => new Map(),
        revalidateOnFocus: false,
        errorRetryCount: 3,
        errorRetryInterval: 2000,
      }}
    >
      <TrackingContext.Provider
        value={
          startTracking
            ? startTracking('Clients', BUILD_TARGET === 'live')
            : undefined
        }
      >
        <AppShellContext.Provider value={{ ...props, startTracking: false }}>
          <Navigation />
          <Router base={props.path}>
            <Switch>
              <Route path="/public/:slugOrId/:blockNum?">
                {(params): ReactElement => (
                  <UserAccessRestrictionProvider>
                    <CwaArticleGetter
                      publishStatus="live"
                      contentType="public"
                      {...params}
                    />
                  </UserAccessRestrictionProvider>
                )}
              </Route>

              <Route path="/preview/:contentType/:slugOrId/:blockNum?">
                {(params): ReactElement => (
                  <AuthWrapper>
                    <UserAccessRestrictionProvider>
                      <CwaArticleGetter publishStatus="preview" {...params} />
                    </UserAccessRestrictionProvider>
                  </AuthWrapper>
                )}
              </Route>

              <Route path="/long-term-preview/:contentType/:slugOrId/:blockNum?">
                {(params): ReactElement => (
                  <AuthWrapper>
                    <UserAccessRestrictionProvider>
                      <CwaArticleGetter
                        publishStatus="long_term_preview"
                        {...params}
                      />
                    </UserAccessRestrictionProvider>
                  </AuthWrapper>
                )}
              </Route>

              <Route path="/:contentType/:slugOrId/:blockNum?">
                {(params): ReactElement => (
                  <AuthWrapper>
                    <UserAccessRestrictionProvider>
                      <CwaArticleGetter publishStatus="live" {...params} />
                    </UserAccessRestrictionProvider>
                  </AuthWrapper>
                )}
              </Route>

              <Route path="/:any*">
                <Error404 />
              </Route>
            </Switch>
          </Router>
        </AppShellContext.Provider>
      </TrackingContext.Provider>
    </SWRConfig>
  );
};

export default App;
